import {
  Input,
  Button,
  Radio,
  Select,
  List,
  Avatar,
  Typography,
  Modal,
  Form,
  Pagination,
  Popover,
  message,
  Flex,
  TagProps,
  Tag,
  Tooltip,
  Space,
  Dropdown,
  Spin,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Sider from "antd/es/layout/Sider";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildHref, formatMoney, formatTime, getFirstAlphabet, parseEmailTitle, toTitleCase } from "utils/functions";
import { SystemEmailStaticTags } from "dtos/email.dto";
import {
  TbBriefcase2,
  TbBuildingCommunity,
  TbChevronDown,
  TbFilter,
  TbInbox,
  TbMessages,
  TbSend,
} from "react-icons/tb";
import { ApiService } from "api/api.service";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConnectAccountModal from "./connect-account-modal";
import { AddEmailsModal } from "./add-emails-modal";
import IncomingMails from "components/svgs/Inbox";
import OutgoingMails from "components/svgs/Outbox";
import { pickAntdTagColorFromSeed } from "utils/colors";
import { MdOutlineKeyboardDoubleArrowLeft } from "react-icons/md";
import { colors } from "./email-message";
import { storedTokens } from "utils/storedTokens";

export const ThreadTags = (props: { tags: string[] }) => {
  const mapping: Record<string, { label: string; color: TagProps["color"] }> = {
    [SystemEmailStaticTags.CREDITOR_COMMUNICATION]: { label: "Creditor", color: "lime-inverse" },
    [SystemEmailStaticTags.DEBTOR_COMMUNICATION]: { label: "Debtor", color: "orange-inverse" },
  };

  const filtered = props.tags.map((tag) => mapping[tag]).filter(Boolean);

  return (
    <Flex className="w-fit max-w-24 gap-1 flex-wrap items-end justify-end">
      {filtered.map((tag) => {
        return (
          <Tag className="!m-0 lowercase text-xs opacity-60 !font-normal" key={tag.label} color={tag.color}>
            {tag.label}
          </Tag>
        );
      })}
    </Flex>
  );
};

export type EmailType = "all" | "inbox" | "outbox";

interface EmailSideBarProps {
  selectedEmailId?: string;
  selectedProfileId?: string;
  setSelectedEmailId: (id: string | undefined) => void;
  closeComposeModal: () => void;
  connectModalOpen: boolean;
  setConnectModalOpen: (open: boolean) => void;
  addModalOpen: boolean;
  setAddModalOpen: (open: boolean) => void;
  refetchProfile: () => void;
  isInboxPanelOpen: boolean;
  closeInboxPanelOpen: () => void;
}

export default function EmailSideBar({
  selectedEmailId,
  setSelectedEmailId,
  closeComposeModal,
  connectModalOpen,
  setConnectModalOpen,
  addModalOpen,
  setAddModalOpen,
  refetchProfile,
  selectedProfileId,
  closeInboxPanelOpen,
  isInboxPanelOpen,
}: Readonly<EmailSideBarProps>) {
  const navigate = useNavigate();
  const { app_key } = useParams<{ app_key: string }>();
  const { orgCode } = useParams<{ orgCode: string }>();
  const storedOrgCode = storedTokens.orgCode.getToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromAppKey = searchParams.get("fromApp") || undefined;
  const selectedLoan = searchParams.get("selectedLoan") || undefined;
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;
  const tab = searchParams.get("tab");
  const composeModalOpen = searchParams.get("compose") === "true";

  const listRef = useRef<HTMLDivElement>(null);
  const [applicationFilter, setApplicationFilter] = useState({
    page: 1,
    limit: 50,
    search: "",
  });
  const allApplicationsQuery = ApiService.application.useGetPaginatedForOrg(applicationFilter);

  const [searchQuery, setSearchQuery] = useState("");
  // const [tags, setTags] = useState<string[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [emailType, setEmailType] = useState<EmailType>(() => {
    const tabs = ["all", "inbox", "outbox"];
    return (tab && tabs.includes(tab) ? tab : tabs[0]) as EmailType;
  });
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [emailForm] = Form.useForm<{ appKey?: string; loanId?: string }>();

  // const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  // const [suggestionFilter, setSuggestionFilter] = useState<{ page: number; limit: number; search: string }>({
  //   page: 1,
  //   limit: 20,
  //   search: "",
  // });
  // const emailSuggestions = ApiService.email.useGetEmailSuggestions({ appKey: app_key, ...suggestionFilter });
  // const suggestedEmail = emailSuggestions.data?.result.data ?? [];
  // const getThreadfilter = ApiService.email.useGetThreadFilter({ profileId: selectedProfileId });
  // const creditorsByAppKey = ApiService.creditor.useGetPaginatedByAppKey({ appKey: app_key, page: 1, limit: 100 });
  // const allCreditors = creditorsByAppKey.data?.result.data ?? [];
  // const creditorFilter = getThreadfilter.data?.result.creditors ?? [];
  // const debtorFilters = getThreadfilter.data?.result.applications ?? [];
  // const [communicationTags, setCommunicationTags] = useState<SystemEmailStaticTags>(
  //   SystemEmailStaticTags.CREDITOR_COMMUNICATION,
  // );

  const defaultFilter = {
    page: page,
    limit: limit,
    search: "",
    appKey: orgCode ? fromAppKey : app_key,
    loanId: selectedLoan,
  };
  const [filter, setFilter] = useState<{
    page: number;
    limit: number;
    search: string;
    loanId: string | undefined;
    appKey: string | undefined;
  }>(() => defaultFilter);
  const { data, isLoading, refetch } = ApiService.email.useGetInfiniteEmails(
    {
      page: filter.page,
      limit: filter.limit,
      search: searchQuery,
      type: emailType,
      matchEmails: selectedEmails,
      orgCode: undefined,
      appKey: app_key, // should never change based on filter
      loanId: filter.loanId,
      profileId: selectedProfileId,
    },
    (params) => !orgCode && Boolean(params.appKey),
  );
  const orgEmails = ApiService.email.useGetInfiniteEmailsForOrg(
    {
      page: filter.page,
      limit: filter.limit,
      search: searchQuery,
      type: emailType,
      matchEmails: selectedEmails,
      tags: [],
      orgCode,
      appKey: filter.appKey, // will change based on filter
      loanId: filter.loanId,
      profileId: selectedProfileId,
    },
    (params) => Boolean(params.orgCode) && Boolean(params.profileId),
  );
  const selectedAppKey = Form.useWatch("appKey", emailForm);
  const selectedLoanId = Form.useWatch("loanId", emailForm);
  const scopedAppKey = orgCode ? (selectedAppKey ?? filter.appKey) : app_key;
  const applicationQuery = ApiService.application.useGetByAppKey(scopedAppKey);
  const loans = applicationQuery.data?.data.application.loans ?? [];

  useEffect(() => {
    if (selectedLoanId && !loans.some((l) => l._id === selectedLoanId)) {
      emailForm.setFieldValue("loanId", undefined);
    }
  }, [applicationQuery.data, selectedLoanId]);

  useEffect(() => {
    setFilter((prev) => ({ ...prev, page: 1, limit, search: "" }));
  }, [selectedProfileId, limit]);

  const emails = useMemo(() => {
    return data?.result.data ?? [];
  }, [data]);
  const allOrgEmails = useMemo(() => {
    return orgEmails?.data?.result.data ?? [];
  }, [orgEmails]);

  const emailsToUse = app_key ? emails : allOrgEmails;
  const queryToUse = useMemo(
    () =>
      app_key
        ? { data, isLoading, refetch, totalObjects: data?.result.totalObjects }
        : {
            data: orgEmails.data,
            isLoading: orgEmails.isLoading,
            refetch: orgEmails.refetch,
            totalObjects: orgEmails.data?.result.totalObjects,
          },
    [app_key, data, isLoading, refetch, orgEmails],
  );

  const inboxOptions = [
    {
      key: "admin",
      label: "Admin Inbox",
      icon: <TbBuildingCommunity size={16} />,
      href: buildHref(`/dashboard/emails/${storedOrgCode}`, {
        fromApp: app_key,
        selectedLoan: selectedLoan,
        compose: composeModalOpen,
      }),
      disabled: !app_key,
    },
    {
      key: "client",
      label: "Client Inbox",
      icon: <TbBriefcase2 size={16} />,
      href: fromAppKey
        ? buildHref(`/dashboard/application/${fromAppKey}/email`, {
            selectedLoan: selectedLoan,
            compose: composeModalOpen,
          })
        : undefined,
      disabled: !fromAppKey,
    },
  ];

  const currentOption = app_key ? inboxOptions[1] : inboxOptions[0];
  const allDisabled = inboxOptions.every((opt) => opt.disabled);

  return (
    <Sider
      style={{
        borderRight: isInboxPanelOpen ? "1px solid #e5e7eb" : undefined,
        transition: "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
      }}
      width={isInboxPanelOpen ? 400 : 0}
      className="bg-white sticky top-0 h-screen "
    >
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center justify-between w-full">
              {allDisabled ? (
                <Typography.Text className="!text-lg !font-medium text-inherit">{currentOption.label}</Typography.Text>
              ) : (
                <Dropdown
                  trigger={allDisabled ? [] : undefined}
                  menu={{
                    items: inboxOptions.map((opt) => {
                      const isCurrent = opt.key === currentOption.key;
                      return {
                        key: opt.key,
                        label: <span className={isCurrent ? "text-indigo-600 font-medium" : ""}>{opt.label}</span>,
                        icon: <span className={isCurrent ? "text-indigo-600 font-medium" : ""}>{opt.icon}</span>,
                        disabled: opt.disabled || isCurrent,
                        onClick: () => {
                          if (opt.href) navigate(opt.href);
                        },
                      };
                    }),
                  }}
                >
                  <Space className="text-gray-500 cursor-pointer select-none hover:text-indigo-600">
                    <Typography.Text className="!text-lg !font-medium text-inherit">
                      {currentOption.label}
                    </Typography.Text>
                    <TbChevronDown className="inline text-sm opacity-60" />
                  </Space>
                </Dropdown>
              )}

              <Tooltip title="Close Inbox">
                <MdOutlineKeyboardDoubleArrowLeft onClick={closeInboxPanelOpen} className="cursor-pointer" />
              </Tooltip>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Input
              placeholder="Search emails..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className=""
              allowClear
            />
            <Popover
              open={isPopoverOpen}
              onOpenChange={(open) => {
                setIsPopoverOpen(open);
              }}
              placement="bottom"
              trigger={"click"}
              style={{ width: 300 }}
              content={
                <Form
                  className="w-[300px]"
                  form={emailForm}
                  initialValues={{
                    appKey: filter.appKey,
                    loanId: filter.loanId,
                  }}
                  layout="vertical"
                >
                  {!app_key && (
                    <Form.Item label="Select Application" name={"appKey"}>
                      <Select
                        allowClear
                        filterOption={false}
                        // mode="multiple"
                        // maxTagCount={"responsive"}
                        // maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                        loading={allApplicationsQuery.isLoading}
                        placeholder="Filters emails by application"
                        onSearch={(value) => setApplicationFilter((prev) => ({ ...prev, search: value }))}
                        notFoundContent={allApplicationsQuery.isLoading ? <Spin size="small" /> : null}
                        options={allApplicationsQuery.data?.result.data.map((app) => ({
                          label: (
                            <Typography.Text>
                              {app.meta.business_name || app.meta.client_name || app.app_key}{" "}
                              <span className="block text-xs opacity-70">
                                {app.enrolled_debt
                                  ? `${formatMoney(app.enrolled_debt)} due across ${app.total_loans ?? 0} loans`
                                  : undefined}
                              </span>
                            </Typography.Text>
                          ),
                          value: app.app_key,
                        }))}
                        labelRender={(prop) => {
                          const app = allApplicationsQuery.data?.result.data.find((app) => app.app_key === prop.value);
                          if (app) return app.meta.business_name || app.meta.client_name || app.app_key;
                          return prop.label;
                        }}
                        onSelect={() => setApplicationFilter((prev) => ({ ...prev, search: "" }))}
                        onChange={() => emailForm.setFieldValue("loanId", undefined)}
                      />
                    </Form.Item>
                  )}

                  <Form.Item label="Select Loan" name={"loanId"}>
                    <Select
                      allowClear
                      filterOption={false}
                      // mode="multiple"
                      // maxTagCount={"responsive"}
                      // maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                      loading={applicationQuery.isLoading}
                      placeholder="Filters emails by loan"
                      onSearch={(value) => setApplicationFilter((prev) => ({ ...prev, search: value }))}
                      notFoundContent={applicationQuery.isLoading ? <Spin size="small" /> : null}
                      options={loans.map((loan) => ({
                        label: (
                          <Typography.Text>
                            {loan.creditor_name}{" "}
                            <span className="block text-xs opacity-70">
                              {loan.due_amount ? `${formatMoney(loan.due_amount)} due` : undefined}
                              {loan.payments
                                ? `, paying ${formatMoney(loan.payments)} ${loan.frequency || "weekly"}`
                                : undefined}
                            </span>
                          </Typography.Text>
                        ),
                        value: loan._id,
                      }))}
                      labelRender={(prop) => {
                        const loan = loans.find((loan) => loan._id === prop.value);
                        return loan?.creditor_name || prop.label;
                      }}
                      onSelect={() => setApplicationFilter((prev) => ({ ...prev, search: "" }))}
                    />
                  </Form.Item>
                  {/* {app_key && (
                    <Form.Item label="Filter by Creditor" name={"creditor"}>
                      <Select
                        styles={{}}
                        mode="multiple"
                        value={communicationTags}
                        maxTagCount={"responsive"}
                        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                        loading={creditorsByAppKey.isPending}
                        style={{ width: "100%" }}
                        placeholder="Select creditor names"
                        onChange={(value) => {
                          setCommunicationTags(value as SystemEmailStaticTags);
                        }}
                        options={allCreditors.map((creditor) => ({ label: creditor.name, value: creditor._id }))}
                      />
                    </Form.Item>
                  )}
                  {orgCode && (
                    <Form.Item label="Filter by Tags" name="email_tags">
                      <Select
                        style={{ width: "100%" }}
                        defaultValue={communicationTags}
                        placeholder="Select Tags"
                        onChange={(values) => {
                          setCommunicationTags(values as SystemEmailStaticTags);

                          emailForm.setFieldValue("debtor_tags", undefined);
                          emailForm.setFieldValue("creditor_tags", undefined);
                        }}
                        options={Object.values(SystemEmailStaticTags).map((tag) => ({
                          label: toTitleCase(tag, { sep: "_" }),
                          value: tag,
                        }))}
                      />
                    </Form.Item>
                  )} */}

                  {/* {orgCode && (
                    <Form.Item label="Filter by Debtor" name="debtor_tags">
                      <Select
                        showSearch
                        optionLabelProp="label"
                        style={{ width: "100%" }}
                        placeholder="Select Debtor"
                        onChange={() => {}}
                        filterOption={(input, option) => {
                          const clientName = option?.clientName?.toLowerCase() || "";
                          const appKey = option?.appKey?.toLowerCase() || "";
                          const search = input.toLowerCase();

                          return clientName.includes(search) || appKey.includes(search);
                        }}
                        options={debtorFilters.map((tag) => ({
                          label: tag.meta.client_name || tag.app_key,
                          value: tag.tag,
                          clientName: tag.meta.client_name,
                          appKey: tag.app_key,
                        }))}
                      />
                    </Form.Item>
                  )} */}

                  {/* {orgCode && (
                    <Form.Item label="Filter by Creditor" name="creditor_tags">
                      <Select
                        optionLabelProp="label"
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Select Creditor"
                        optionFilterProp="label"
                        onChange={() => {}}
                        options={creditorFilter.map((tag) => ({
                          label: tag.name,
                          value: tag.tag,
                        }))}
                      />
                    </Form.Item>
                  )} */}
                  <div>
                    <Form.Item className="flex justify-end mb-0">
                      <Button
                        onClick={() => {
                          // setSelectedEmails([]);
                          // setTags([]);
                          emailForm.resetFields();
                          setIsPopoverOpen(false);
                        }}
                        className="mr-2"
                      >
                        Reset
                      </Button>
                      <Button
                        type="primary"
                        onClick={async () => {
                          const values = await emailForm.validateFields();
                          setFilter((prev) => ({ ...prev, appKey: values.appKey, loanId: values.loanId }));
                          // const selectedCreditorEmails = allCreditors
                          //   .filter((creditor) => values.creditor?.includes(creditor._id))
                          //   .flatMap((creditor) => creditor.allowed_emails);
                          // const allSelectedEmails = Array.from(
                          //   new Set([...(values.emails || []), ...selectedCreditorEmails]),
                          // );

                          // const allValues = emailForm.getFieldsValue(true); // true = include unmounted fields
                          // const debtorTag = allValues.debtor_tags;
                          // const creditorTag = allValues.creditor_tags;

                          // if (debtorTag === undefined && creditorTag === undefined) {
                          //   message.error("Please select at least one tag.");
                          //   return;
                          // }

                          // if (debtorTag) {
                          //   setTags([debtorTag]);
                          // }
                          // if (creditorTag) {
                          //   setTags([...tags, creditorTag]);
                          // }

                          // setSelectedEmails(allSelectedEmails);
                          setIsPopoverOpen(false);
                        }}
                      >
                        Apply Filter
                      </Button>
                    </Form.Item>
                  </div>
                </Form>
              }
            >
              <Button
                className={`${selectedEmails.length > 0 ? "bg-blue-100 text-blue-500" : ""}`}
                icon={<TbFilter />}
              />
            </Popover>
          </div>
          {/* Filter */}
          <Radio.Group
            buttonStyle="solid"
            optionType="button"
            value={emailType}
            onChange={(e) => {
              setEmailType(e.target.value);
              setFilter((prev) => ({ ...prev, page: 1 }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", "1");
                newParams.set("tab", e.target.value);

                return newParams;
              });
            }}
            className="w-full flex"
          >
            <Radio.Button value="all" className="w-full text-center text-nowrap">
              <TbMessages className="inline mr-1" />
              All Mails
            </Radio.Button>
            <Radio.Button value="inbox" className="w-full text-center text-nowrap">
              <TbInbox className="inline mr-1" />
              Inbox
            </Radio.Button>
            <Radio.Button value="outbox" className="w-full text-center text-nowrap">
              <TbSend className="inline mr-1" />
              Sent
            </Radio.Button>
          </Radio.Group>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto " style={{ maxHeight: "calc(100vh - 250px)" }}>
          {queryToUse.isLoading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-4 p-4">
                  <div className="rounded-full bg-gray-300 h-10 w-10"></div>
                  <div className="flex-1 space-y-4 py-1">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-300 rounded"></div>
                      <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : emailsToUse.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 text-nowrap">
              {emailType === "outbox" ? <OutgoingMails /> : <IncomingMails />}
            </div>
          ) : (
            <List
              dataSource={emailsToUse}
              renderItem={(email) => {
                const rawName = parseEmailTitle(email?.lastSentByName);
                const safeName = typeof rawName === "string" && rawName.trim() ? rawName : "Unknown";

                const displayName = safeName.split("@")[0];
                const firstChar = getFirstAlphabet(safeName)?.toUpperCase() || "U";
                const colorIndex = (firstChar.charCodeAt(0) - 65) % colors.length;

                return (
                  <List.Item
                    key={email._id}
                    className={`cursor-pointer px-4 py-3 transition-all ${
                      selectedEmailId === email?.externalId ? "bg-indigo-50" : ""
                    } hover:bg-indigo-50`}
                    onClick={() => {
                      closeComposeModal();
                      setSelectedEmailId(email?.externalId);
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <div className="flex w-full items-center gap-3 px-4">
                          <Avatar
                            style={{
                              backgroundColor: colors[colorIndex][email?.hasRead ? "bg" : "text"],
                              color: colors[colorIndex][email?.hasRead ? "text" : "bg"],
                              width: 36,
                              aspectRatio: 1,
                            }}
                            className={`uppercase ${email?.hasRead ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {firstChar}
                          </Avatar>

                          <div className="flex items-center w-full justify-between">
                            <Typography.Text
                              className={`text-sm font-semibold ${email?.hasRead ? "text-gray-400" : "text-gray-600"}`}
                            >
                              {displayName}
                              <span className="block text-xs font-normal">{email?.lastSentByEmail || "—"}</span>
                            </Typography.Text>
                            <Flex vertical gap={2}>
                              <Typography.Text className="text-xs text-end text-gray-400">
                                {email?.lastMessagedAt ? formatTime(email.lastMessagedAt) : ""}
                              </Typography.Text>
                              {email.creditor?.[0]?.name ? (
                                <Tooltip title={email.creditor.map((c) => c.name).join(", ")}>
                                  <Tag
                                    className="!mr-0 max-w-20"
                                    color={pickAntdTagColorFromSeed(email.creditor[0].name)}
                                  >
                                    <Typography.Text className="!text-xs text-inherit uppercase" ellipsis>
                                      {email.creditor[0].name}
                                    </Typography.Text>
                                  </Tag>
                                </Tooltip>
                              ) : null}
                              {/* <ThreadTags tags={email.internalTags ?? []} /> */}
                            </Flex>
                          </div>
                        </div>
                      }
                      description={
                        <div className={`px-4 ${email?.hasRead ? "opacity-70" : "opacity-100"}`}>
                          <Typography.Paragraph type="secondary" style={{ margin: 0 }} className="text-sm truncate">
                            {email?.subject || "No subject"}
                          </Typography.Paragraph>

                          <Typography.Paragraph type="secondary" style={{ margin: 0 }} className="text-xs truncate">
                            {email?.snippet || ""}
                          </Typography.Paragraph>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </div>
        <div className="px-6 my-4">
          <Pagination
            hideOnSinglePage={true}
            align="center"
            size="small"
            total={queryToUse.totalObjects}
            showSizeChanger
            current={filter.page}
            pageSize={filter.limit}
            onChange={(page, pageSize) => {
              setFilter((prev) => ({ ...prev, page, limit: pageSize }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", page.toString());
                newParams.set("limit", pageSize.toString());
                return newParams;
              });
            }}
          />
        </div>
      </div>
      <DomainModal isOpen={isDomainModalOpen} onClose={() => setIsDomainModalOpen(false)} />
      <ConnectAccountModal
        setSelectedEmail={() => setSelectedEmailId(undefined)}
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        refetchEmails={() => queryToUse.refetch()}
        refetchProfile={refetchProfile}
      />
      <AddEmailsModal appKey={app_key} open={addModalOpen} onCancel={() => setAddModalOpen(false)} />
      {/* <Modal
        title="Filter by Emails"
        open={isEmailModalOpen}
        onCancel={() => {
          setIsEmailModalOpen(false);
          setSelectedEmails([]);
          emailForm.resetFields();
        }}
        cancelText="Reset"
        okText="Apply"
        onOk={async () => {
          const values = await emailForm.validateFields();
          setSelectedEmails(values.emails);
          setIsEmailModalOpen(false);
        }}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item name={"emails"}>
            <Select
              mode="tags"
              loading={emailSuggestions.isPending}
              style={{ width: "100%" }}
              placeholder="Enter email addresses"
              onChange={() => {
                // Reset search filter when an email is selected
                setSuggestionFilter((prev) => ({ ...prev, search: "" }));
              }}
              onSearch={(value) => {
                setSuggestionFilter((prev) => ({ ...prev, search: value }));
              }}
              options={suggestedEmail.map((email) => ({ label: email, value: email }))}
            />
          </Form.Item>
        </Form>
      </Modal> */}
    </Sider>
  );
}

const DomainModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  return (
    <Modal title="Filter by Domains" open={isOpen} onCancel={onClose} footer={null}>
      <Select mode="multiple" style={{ width: "100%" }} placeholder="Select domains">
        {/* Options would be populated dynamically */}
      </Select>
    </Modal>
  );
};
